
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Users, FileText, UserCheck, UserX } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface StatsData {
  total_listings: number;
  total_breeders: number;
  total_buyers: number;
  total_users: number;
}

interface DailySignups {
  date: string;
  signups: number;
}

interface UserTypeDistribution {
  name: string;
  value: number;
  color: string;
}

const fillMissingDates = (data: DailySignups[], timeRange: 'daily' | 'monthly'): DailySignups[] => {
  if (!data || data.length === 0) return [];

  const today = new Date();
  const filledData: DailySignups[] = [];

  if (timeRange === 'daily') {
    // Fill last 7 days
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const existingData = data.find(item => item.date === dateStr);
      filledData.push({
        date: dateStr,
        signups: existingData ? existingData.signups : 0
      });
    }
  } else {
    // Fill last 12 months
    for (let i = 11; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const dateStr = date.toISOString().split('T')[0].substring(0, 7) + '-01';
      
      const existingData = data.find(item => item.date.startsWith(dateStr.substring(0, 7)));
      filledData.push({
        date: dateStr,
        signups: existingData ? existingData.signups : 0
      });
    }
  }

  return filledData;
};

export const StatisticsSection = () => {
  const [timeRange, setTimeRange] = useState<'daily' | 'monthly'>('daily');

  // Fetch general statistics
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_admin_statistics' as any);
      if (error) throw error;
      return data?.[0] as StatsData;
    },
  });

  // Fetch signup trends
  const { data: signupTrends, isLoading: trendsLoading } = useQuery({
    queryKey: ['admin-signup-trends', timeRange],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_signup_trends' as any, { 
          time_range: timeRange 
        });
      if (error) throw error;
      const filledData = fillMissingDates(data as DailySignups[], timeRange);
      return filledData;
    },
  });

  // Fetch user type distribution
  const { data: userDistribution, isLoading: distributionLoading } = useQuery({
    queryKey: ['admin-user-distribution'],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_user_type_distribution' as any);
      if (error) throw error;
      
      const colors = ['#10B981', 'hsl(var(--foreground))'];
      return (data as any[])
        .filter(item => item.user_type !== 'admin') // Filter out admins
        .map((item, index) => ({
          name: item.user_type === 'breeder' ? 'Breeders' : 'Buyers',
          value: parseInt(item.count),
          color: colors[index] || 'hsl(var(--muted-foreground))'
        })) as UserTypeDistribution[];
    },
  });

  const chartConfig = {
    signups: {
      label: "Signups",
      color: "#10B981",
    },
  };

  if (statsLoading || trendsLoading || distributionLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Statistics</h1>
        <p className="text-gray-600 mt-1">Platform overview and analytics</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_users || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Listings</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_listings || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Breeders</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_breeders || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Buyers</CardTitle>
            <UserX className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total_buyers || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Signup Trends Chart - 2 columns wide */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
              <div>
                <CardTitle>User Signups</CardTitle>
                <CardDescription>
                  New user registrations over time
                </CardDescription>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant={timeRange === 'daily' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTimeRange('daily')}
                >
                  Daily
                </Button>
                <Button
                  variant={timeRange === 'monthly' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTimeRange('monthly')}
                >
                  Monthly
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pl-2">
            <ChartContainer config={chartConfig} className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={signupTrends} margin={{ top: 5, right: 30, left: -20, bottom: 5 }}>
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return timeRange === 'daily' 
                        ? date.toLocaleDateString('en-US', { weekday: 'short' })
                        : date.toLocaleDateString('en-US', { month: 'short' });
                    }}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    allowDecimals={false}
                    domain={[0, 'dataMax']}
                  />
                  <ChartTooltip 
                    content={<ChartTooltipContent />}
                    labelFormatter={(value) => {
                      const date = new Date(value);
                      return timeRange === 'daily'
                        ? date.toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            month: 'long', 
                            day: 'numeric' 
                          })
                        : date.toLocaleDateString('en-US', { 
                            month: 'long', 
                            year: 'numeric' 
                          });
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="signups" 
                    stroke="var(--color-signups)" 
                    strokeWidth={2}
                    dot={{ fill: "var(--color-signups)", r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* User Distribution Pie Chart - 1 column wide */}
        <Card>
          <CardHeader>
            <CardTitle>User Distribution</CardTitle>
            <CardDescription>
              Breakdown of user types on the platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={{}} className="h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={userDistribution}
                    cx="50%"
                    cy="45%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {userDistribution?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <ChartTooltip 
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <div className="bg-background border rounded-lg p-2 shadow-lg">
                            <p className="font-medium">{data.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {data.value} users ({((data.value / (userDistribution?.reduce((sum, item) => sum + item.value, 0) || 1)) * 100).toFixed(0)}%)
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <ChartLegend 
                    content={(props) => (
                      <div className="flex justify-center gap-4 mt-4">
                        {userDistribution?.map((entry, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-sm" 
                              style={{ backgroundColor: entry.color }}
                            />
                            <span className="text-sm text-muted-foreground">{entry.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
